import { useQuery } from "@apollo/client";
import Alert from "@mui/material/Alert";
import { altIdTypesFor } from "encamp-shared/src/violations/expiredAltIdMatchKinds";
import { useCurrentUser } from "hooks/useCurrentUser";
import { HELD_EXPIRED_ALT_ID_MATCHES_FOR_FACILITY } from "hooks/useExpiredAltIdMatchReviews";

/**
 * Warns before linking an ID that would release violations Encamp is still
 * reviewing.
 *
 * Linking writes an alternate ID with no expiry — active — so if a held match
 * group rests on the same value, the next harmonize run releases every violation
 * against it. Reachable in practice when the group is orphaned: its ID row was
 * deleted rather than expired, which is exactly when this screen offers to
 * re-link the value.
 *
 * Both audiences see it, for opposite reasons. A customer's save will be refused,
 * so the warning explains a rejection before they hit it. A staff member's save
 * will succeed, irreversibly, so the warning is the informed half of "staff are
 * informed, never blocked".
 *
 * `severity="warning"`, never `error` — the smoke suite fails a page carrying an
 * error Alert.
 */
export function HeldMatchLinkWarning({
  facilityId,
  candidateValue,
  alternateIdType,
}: {
  facilityId: string;
  candidateValue?: string;
  /** The alternate-id type this screen is about to write (EPA / FRS / NPDES). */
  alternateIdType: string;
}) {
  const { isStaff } = useCurrentUser();
  const { data } = useQuery(HELD_EXPIRED_ALT_ID_MATCHES_FOR_FACILITY, {
    variables: { facilityId },
    skip: !facilityId,
  });

  // Match on value AND type: the two match kinds partition the space, so an
  // FRS-typed link cannot release a WASTE_HANDLER group. Warning on value alone
  // would claim a refusal the gate would not actually make.
  const matched = (data?.heldExpiredAltIdMatchesForFacility ?? []).filter(
    (group) =>
      group.altIdValue === candidateValue &&
      altIdTypesFor(group.matchKind).includes(alternateIdType)
  );
  if (!candidateValue || matched.length === 0) {
    return null;
  }

  const violationCount = matched.reduce((total, group) => total + group.violationCount, 0);
  const plural = violationCount === 1 ? "violation" : "violations";

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      {isStaff
        ? `Encamp is holding ${violationCount} ${plural} matched to ${candidateValue} for expired-ID
           review. Linking releases them to this customer immediately, records you as the person who
           released them, and sends any unresolved ones to their Airtable with no way to retract.`
        : `Encamp is reviewing ${violationCount} ${plural} matched to ${candidateValue}. Linking this
           ID is blocked until that review finishes — contact Encamp to have it released.`}
    </Alert>
  );
}
